// components/ScrollableAutocomplete.tsx
import React, { useState } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface ScrollableAutocompleteProps {
  value: any;
  onChange: (newValue: any) => void;
  options: any[];
  keyName: string;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onScroll?: (event: React.UIEvent<HTMLUListElement>) => void;
  limitTags?: number;
  disableSearch?: boolean;
  addOptionText?: string;
  onAddOptionClick?: () => void;
  showAddOption?: boolean;
  getOptionLabel?: any;
  isEpisode?: boolean;
}

const ScrollableAutocomplete: React.FC<ScrollableAutocompleteProps> = ({
  value,
  onChange,
  options,
  keyName,
  placeholder = '',
  multiple = false,
  disabled = false,
  loading = false,
  onScroll,
  limitTags = 1,
  disableSearch = false,
  addOptionText,
  onAddOptionClick,
  showAddOption = false,
  getOptionLabel,
  isEpisode,
}) => {
  const [inputValue, setInputValue] = useState('');

  const finalOptions = [...options];

  if (loading) {
    finalOptions.push({ isLoading: true });
  }

  if (showAddOption && addOptionText) {
    finalOptions.unshift({ name: addOptionText, isNew: true });
  }

  const defaultGetOptionLabel = (option: any) => {
    if (keyName === 'address' && typeof option[keyName] === 'object') {
      const address = option[keyName];
      return address?.addressLine2
        ? `${address.addressLine1}, ${address.addressLine2}`
        : `${address.addressLine1}`;
    } else if (isEpisode) {
      return `${option?.episodeNumber ? `${option?.episodeNumber} - ` : ''}${
        option?.[keyName]
      }`;
    }
    return option?.[keyName] ?? '';
  };

  return (
    <Autocomplete
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disableClearable
      disablePortal
      popupIcon={<KeyboardArrowDownIcon />}
      ListboxProps={{
        onScroll: onScroll,
      }}
      disabled={disabled}
      options={finalOptions}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      isOptionEqualToValue={(option, val) => {
        if (keyName === 'address') {
          const optionAddress = option?.[keyName];
          const valAddress = val?.[keyName];
          return optionAddress?._id === valAddress?._id;
        }
        return option?._id === val?._id;
      }}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason !== 'reset') {
          setInputValue(newInputValue);
        }
      }}
      renderOption={(props, option, { selected }) => {
        if (option.isLoading) {
          return (
            <li {...props}>
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                Loading...
              </Box>
            </li>
          );
        }

        {
          console.log(multiple, 'multiple');
        }

        return (
          <li {...props} className="filters-dropdown">
            <div className="checkbox">
              {multiple && (
                <input type="checkbox" checked={selected} readOnly />
              )}
              <label>
                <span>
                  {keyName === 'address'
                    ? option?.[keyName]?.addressLine2
                      ? `${option?.[keyName]?.locationName}, ${option?.[keyName]?.addressLine1}, ${option?.[keyName]?.cityTown}, ${option?.[keyName]?.addressLine2}`
                      : `${option?.[keyName]?.locationName}, ${option?.[keyName]?.addressLine1}, ${option?.[keyName]?.cityTown}`
                    : isEpisode
                    ? `${
                        option?.episodeNumber
                          ? option?.episodeNumber + ' - '
                          : ''
                      }${option?.name}`
                    : option?.[keyName]}
                </span>
              </label>
            </div>
          </li>
        );
      }}
      value={value}
      onChange={(_, newValue) => {
        onChange(newValue);

        // Check if all options matching the current input are selected
        const matchedOptions = finalOptions.filter(opt =>
          (getOptionLabel || defaultGetOptionLabel)(opt)
            .toLowerCase()
            .includes(inputValue.toLowerCase()),
        );

        const selectedIds = (multiple ? newValue : [newValue])?.map(
          (item: any) => item?._id,
        );

        const allMatchedSelected = matchedOptions.every(opt =>
          selectedIds.includes(opt?._id),
        );

        // Clear input only if all matched options are selected
        if (allMatchedSelected) {
          setInputValue('');
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={Array.isArray(value) && value.length ? '' : placeholder}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            onKeyDown: e => {
              if (disableSearch) e.preventDefault();
            },
          }}
        />
      )}
    />
  );
};

export default ScrollableAutocomplete;
